import { Currency } from "../models/currency";
import { EstimateGroup, EstimatePrice, EstimateService } from "../models/estimate";
import { Material } from "../models/material";
import { Measurement } from "../models/measurement";
import { PriceGroup } from "../models/price.group";
import { RoomJob } from "../models/sales";
import { ChargeOptions, ChargeOptionsEnum, Service as ServiceBooking } from "../models/service";
import { User } from "../models/user";
import { DESTINATION } from "./enums";
import groupBy from "./groupBy";
import { getTotalItemsValuesByInventory } from "./inventory";

interface CalculateSubtotalInterface {
  charge?: keyof ChargeOptions;
  rate?: number;
  quantity?: number;
  discount?: number;
  margin?: number;
  markup?: number;
  volume?: number;
}
interface SymbolTotal {
  [symbol: string]: number;
}

export interface TotalPriceCost {
  materials: number;
  estimate: SymbolTotal;
}

export const currencyToNumber = (formattedCurrency?: string) => {
  if (!formattedCurrency) return 0;

  const cleanNumber = formattedCurrency.replace(/,/g, "");
  return parseFloat(cleanNumber);
};

export const formatCurrency = (
  currencyValue: string | number,
  currency?: Currency,
  showSymbol = true
) => {
  if (Number.isNaN(Number(currencyValue))) return currencyValue;

  const DEFAULT_CURRENCY_CODE = "USD";
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: DEFAULT_CURRENCY_CODE,
  });

  const initialFormat = formatter.format(Number(currencyValue || 0));

  if (!currency) {
    if (showSymbol) return initialFormat;

    return initialFormat.replace("$", "");
  }

  let { decimalSeparator, thousandSeparator, symbol } = currency;

  if (!decimalSeparator) decimalSeparator = ".";
  if (!thousandSeparator) thousandSeparator = ".";
  if (!symbol) symbol = ".";

  let formatted = "";

  for (const letter of initialFormat) {
    if (letter === "$") {
      if (showSymbol) formatted += symbol;
      // formatted += " ";
      continue;
    }
    if (letter === ".") {
      formatted += decimalSeparator;
      continue;
    }
    if (letter === ",") {
      formatted += thousandSeparator;
      continue;
    }
    formatted += letter;
  }

  return formatted;
};

export const calculateSubtotal = ({
  charge,
  rate = 0,
  quantity = 0,
  discount = 0,
  margin = 0,
  markup = 0,
  volume = 0,
}: CalculateSubtotalInterface) =>
  calculateSubtotalByChargeAndByEstimate(charge, rate, quantity, discount, margin, markup, volume);

export const calculateSubtotalByChargeAndByEstimate = (
  charge?: keyof ChargeOptions,
  rate = 0,
  quantity = 0,
  discount = 0,
  margin = 0,
  markup = 0,
  volume = 0
) => {
  const realQuantity = charge
    ? getQuantityByCharge(charge, Number(quantity || 0), Number(volume || 0))
    : Number(quantity || 0);

  return calculateSubToTalByEstimateData(rate, realQuantity, discount, margin, markup);
};

const calculateSubToTalByEstimateData = (
  rate = 0,
  qty = 0,
  discount = 0,
  _margin = 0,
  markup = 0
) => {
  let total = rate * qty;

  // TODO Felipe code commented because in this moment, Carlos said to not calculate margin and markup in subTotal
  // let toMore = Number(margin || 0) + Number(markup || 0);

  // if (Number(toMore)) {
  //   toMore = Number(toMore) / 100;
  //   total += total * toMore;
  // }
  // if (Number(margin)) {
  //   margin = Number(margin) / 100;
  //   total += total * margin;
  // }
  if (Number(markup)) {
    markup = Number(markup) / 100;
    total += total * markup;
  }

  if (Number(discount)) {
    discount = Number(discount) / 100;
    const effectiveDiscount = total * discount;

    total -= effectiveDiscount;
  }

  return total;
};

export const getMaterialsPrice = (materials: Material[]) => {
  let quantity = 0;
  let price = 0;

  materials.forEach((material) => {
    if (material?.destination?.name !== DESTINATION.NOT_GOING) {
      quantity += Number(material.quantity);
      price += Number(material?.price || 0) * Number(material.quantity);
    }
  });

  return { quantity, price };
};

export const getDimensionsFeeOption = (label: string, measurement?: Measurement) => {
  const fee = label.replace("{dimension}", measurement?.code || "").split(" ");
  const feeCode = fee[0].toUpperCase();

  return `${feeCode} ${fee[1]}`;
};

export const getEstimateServiceArrayDefault = (
  companyId?: number,
  currency?: Currency
): EstimateService[] => {
  return [
    {
      companyId,
      currencyId: currency?.id,
      currency,
      discount: 0,
      margin: 0,
      markUp: 0,
      quantity: 1,
    },
  ];
};

export const getFeePropertyByServiceDefaultFee = (service?: ServiceBooking) => {
  if (!service || !service.defaultFee) return null;
  switch (service.defaultFee) {
    case ChargeOptionsEnum.Fee:
      return "m3Fee";
    case ChargeOptionsEnum.Flat:
      return "flatFee";
    case ChargeOptionsEnum.Hourly:
      return "hourlyFee";
    case ChargeOptionsEnum["Half Hour"]:
      return "halfHourFee";
    default:
      return "";
  }
};

export const checkValueMoreThan0 = (value?: string | number, defaultValue?: string | number) => {
  return value && !Number.isNaN(Number(value)) && Number(value) > 0 ? value : defaultValue;
};

export const getRateByChargeSelected = (
  charge?: keyof ChargeOptions,
  service?: ServiceBooking,
  defaultValue?: string | number
) => {
  if (!charge || !service) return defaultValue;
  switch (charge) {
    case ChargeOptionsEnum.Fee:
      return checkValueMoreThan0(service?.sellM3Fee, defaultValue);
    case ChargeOptionsEnum.Flat:
      return checkValueMoreThan0(service?.sellFlatFee, defaultValue);
    case ChargeOptionsEnum.Hourly:
      return checkValueMoreThan0(service?.sellHourlyFee, defaultValue);
    case ChargeOptionsEnum["Half Hour"]:
      return checkValueMoreThan0(service?.sellHalfHourFee, defaultValue);
    default:
      return defaultValue;
  }
};

export const mountEstimatePricesByGroups = (
  groups?: PriceGroup[],
  currentUser?: User,
  agentId?: number
) => {
  const pricesMounted: EstimatePrice[] = [];
  const company = currentUser?.company;
  const companyId = company?.id;
  const currency = company?.currency;

  if (groups && groups.length > 0) {
    const localEstimateGroups: EstimateGroup[] = [];

    for (const group of groups) {
      localEstimateGroups.push({
        name: String(group.name),
        companyId,
        estimateServices: group.services
          ? group.services?.map(
              (service) =>
                ({
                  charge: service.defaultFee,
                  companyId,
                  currency,
                  currencyId: currency?.id,
                  margin: service.margin,
                  name: service.sellDescription,
                  rate: getRateByChargeSelected(service.defaultFee as keyof ChargeOptions, service),
                  quantity: 1,
                  service,
                  serviceId: service.id,
                } as EstimateService)
            )
          : [],
      });
    }
    pricesMounted.push({
      companyId,
      sequence: 1,
      agentId,
      jobEstimateId: undefined,
      groups: localEstimateGroups,
    });
  }

  return pricesMounted;
};

export const getQuantityByCharge = (
  charge: keyof ChargeOptions,
  quantity?: number,
  volume?: number
) => {
  if (charge && charge === ChargeOptionsEnum.Fee) return volume || 0;

  return quantity || 0;
};

const getTotalByGroupGroupingByCurrency = (
  group?: EstimateGroup,
  currencies?: Currency[],
  volume?: number
) => {
  if (group) {
    const estimates = group.estimateServices || [];
    const currencyTotal: { [symbol: string]: number } = {};

    const grouppedByCurrency: { [x: string]: EstimateService[] } = groupBy(estimates, "currencyId");

    if (grouppedByCurrency && Object.keys(grouppedByCurrency).length > 0) {
      Object.keys(grouppedByCurrency).forEach((currencyId) => {
        const currency = currencies?.find((currency) => String(currency.id) === String(currencyId));

        if (currency && currency.code) {
          const estimatesGroupped = grouppedByCurrency[currencyId] || [];
          const total = estimatesGroupped?.reduce((prev, curr) => {
            const subTotal = calculateSubtotalByChargeAndByEstimate(
              curr.charge as keyof ChargeOptions,
              curr.rate,
              curr.quantity,
              curr.discount,
              curr.margin,
              curr.markUp,
              volume
            );

            return prev + Number(subTotal || 0);
          }, 0);

          currencyTotal[currency.code] = total;
        }
      });
    }

    return currencyTotal;
  }

  return null;
};

export const getTotalCostsByPrice = (
  price?: EstimatePrice,
  inventory?: RoomJob[],
  inventoryCurrency?: Currency,
  allCurrencies?: Currency[],
  measurement?: Measurement
): TotalPriceCost => {
  const materials: Material[] = [];
  const materialsInventory = inventory?.map((inventory) => inventory?.materials) || [];

  materialsInventory.forEach((materialInventory) => {
    materials.push(...materialInventory);
  });
  const { price: materialTotalPrice } = getMaterialsPrice(materials);
  const { volumeConverted } = getTotalItemsValuesByInventory(inventory, measurement);

  const symbolTotal: SymbolTotal = {};

  if (price && price.groups && price.groups.length > 0) {
    for (const group of price.groups) {
      const totalSymbolCurrency = getTotalByGroupGroupingByCurrency(
        group,
        allCurrencies,
        volumeConverted
      );

      if (totalSymbolCurrency && Object.keys(totalSymbolCurrency).length > 0) {
        Object.keys(totalSymbolCurrency).forEach((symbol) => {
          symbolTotal[symbol] =
            Number(symbolTotal[symbol] || 0) + Number(totalSymbolCurrency[symbol] || 0);
        });
      }
    }
  }

  if (materialTotalPrice > 0 && inventoryCurrency && inventoryCurrency.code) {
    const { code } = inventoryCurrency;

    symbolTotal[code] = Number(symbolTotal[code] || 0) + Number(materialTotalPrice || 0);
  }

  return {
    estimate: symbolTotal,
    materials: materialTotalPrice,
  };
};

const CHARACTERS_QUANTITY_ALLOWED = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

export const getPriceStringValid = (value?: string, decimalSeparator?: string) => {
  if (!value) return "";
  let newValue = "";

  for (const letter of value) {
    if (CHARACTERS_QUANTITY_ALLOWED.includes(letter)) newValue += letter;
    if (letter === decimalSeparator || letter === ".") newValue += ".";
  }

  return newValue;
};

export const getMarginAndMarkupByRate = (
  value?: number,
  markup?: string | number,
  margin?: string | number
): { margin: number; markup: number } => {
  // $5 Margin ÷ $8 Cost = 62.5% Markup Percentage
  const rate = Number(value || 0) || 0;
  let newMarkup = 0;
  let newMargin = 0;

  if (!rate) return { margin: newMargin, markup: newMarkup };

  if (markup && Number(markup) && Number(markup) > 0) {
    newMarkup = Number(markup);
    newMargin = (Number(markup) / rate) * 100;
  }
  if (margin && Number(margin) && Number(margin) > 0) {
    newMargin = Number(margin);
    newMarkup = (Number(margin) / 100) * rate;
  }

  return { margin: newMargin, markup: newMarkup };
};

const mountEstimateServicesByPriceGroup = (
  group?: PriceGroup,
  companyId?: number,
  currency?: Currency
): EstimateService[] => {
  const defaultServices = getEstimateServiceArrayDefault(companyId, currency);

  if (!group || !group.services || !group.services.length) {
    return defaultServices;
  }

  const groupServices = group?.services || [];

  if (!groupServices || !groupServices.length) {
    return defaultServices;
  }

  return groupServices.map((service) => {
    return {
      companyId,

      serviceId: service.id,
      service,

      currencyId: currency?.id,
      currency,

      name: service.sellDescription,
      charge: service?.defaultFee,

      // margin: service?.margin || 0,
      // markUp: 0,
      // discount: 0,
    } as EstimateService;
  });
};

type MountNewGroupParams = {
  /**
   * Price group with their services;
   */
  group?: PriceGroup;
  /**
   * Company id from user logged;
   */
  companyId?: number;
  /**
   * Currency default to set in services;
   */
  currency?: Currency;
  /**
   * If none group be sent, will use this property as group name;
   */
  defaultGroupName?: string;
};

export const mountNewGroup = (params: MountNewGroupParams): EstimateGroup => {
  const { group, companyId, currency, defaultGroupName } = params;
  const groupName = group?.name || defaultGroupName || "";

  return {
    name: groupName,
    estimateServices: mountEstimateServicesByPriceGroup(group, companyId, currency),
  };
};
